import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { apolloClient } from '@/app/providers/apollo';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { ListDeviceRssiDocument } from '../__generated__/graphql';
import { deviceRssiReadingsListSchema, type DeviceRssiReading } from '../lib/device.schema';

const POLL_INTERVAL_MS = 5 * 60_000;
const RECENT_WINDOW_MS = 15 * 60_000;
const DEFAULT_LIMIT = 5;

type RssiStatus = 'idle' | 'loading' | 'ready';

interface RssiState {
  readings: DeviceRssiReading[];
  status: RssiStatus;
  start: () => void;
  stop: () => void;
  getCandidateGateways: (mac: string, options?: { maxAgeMs?: number; limit?: number }) => string[];
}

let subscription: { unsubscribe: () => void } | null = null;

const normalizeMac = (mac: string): string => mac.replace(/[^0-9a-fA-F]/g, '').toLowerCase();

export const useRssiStore = create<RssiState>()(
  devtools(
    immer((set, get) => ({
      readings: [],
      status: 'idle',

      start: () => {
        if (subscription) {
          return;
        }
        set((s) => {
          s.status = 'loading';
        });

        const observable = apolloClient.watchQuery({
          query: ListDeviceRssiDocument,
          variables: { input: {} },
          pollInterval: POLL_INTERVAL_MS,
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: false,
        });

        subscription = observable.subscribe({
          next: (result) => {
            const data = result.data?.listDeviceRssi ?? [];
            const parsed = deviceRssiReadingsListSchema.parse(data);
            set((s) => {
              s.readings = parsed;
              s.status = 'ready';
            });
          },
          error: (err) => {
            console.error('[rssi-store] subscription error', err);
          },
        });
      },

      stop: () => {
        subscription?.unsubscribe();
        subscription = null;
        set((s) => {
          s.readings = [];
          s.status = 'idle';
        });
      },

      getCandidateGateways: (mac, options) => {
        const maxAgeMs = options?.maxAgeMs ?? RECENT_WINDOW_MS;
        const limit = options?.limit ?? DEFAULT_LIMIT;

        const target = normalizeMac(mac);
        if (!target) {
          return [];
        }

        const now = Date.now();
        return get()
          .readings.filter(
            (r) =>
              normalizeMac(r.device_mac) === target &&
              now - new Date(r.updated_at).getTime() < maxAgeMs,
          )
          .sort((a, b) => b.rssi - a.rssi)
          .slice(0, limit)
          .map((r) => normalizeMac(r.gateway_mac));
      },
    })),
    { name: 'rssi-store' },
  ),
);

if (useAuthStore.getState().isAuthenticated()) {
  useRssiStore.getState().start();
}
useAuthStore.subscribe((state) => {
  if (state.isAuthenticated()) {
    useRssiStore.getState().start();
  } else {
    useRssiStore.getState().stop();
  }
});
